/**
 * Sale — the catalog guest-issue.view.ts draws from: which event is
 * selected, and that event's ticket types. Both reads are public
 * (Event/TicketType carry no permission gate), so this file carries no
 * "denied" status — unlike quota (events/stats.state.ts's own
 * eventSaleStats), which needs tickets.reports.view and lives elsewhere.
 * Pure; sale.sync.ts (web-side) is the sole writer of saleEvents/
 * saleTicketTypes/saleCatalogStatus. selectedSaleEventId is the one field
 * with more than one writer by design — event-console.view.ts pins it for
 * that event's whole lifetime, and guest-issue.view.ts's own picker writes
 * it on a deliberate user choice whenever nothing is pinned.
 *
 * Also the home of canIssueGuestTickets (guest-issue-view's own route gate,
 * mirroring events.state.ts's canManageEvents and members.state.ts's
 * canViewMembers) and canViewSaleReports — no longer a route gate itself,
 * but still the one shared tickets.reports.view check stats.sync.ts
 * (web-side) reads before fetching the events-list footer's own figures.
 */

import { expandPermission } from "@mhmo91/ican";
import { computed, state } from "@mhmo91/schmancy/state";
import type { Event, TicketType } from "@momo/types";
import { activeActor } from "../../auth/actor.state.ts";

export type SaleEventOption = {
	readonly id: string;
	readonly name: string;
	readonly status: "draft" | "published" | "archived";
	/** schedule.start, epoch ms — the picker's own sort key and the auto-select's "soonest upcoming" test. */
	readonly startMs: number;
	/** Carried straight through from the already-parsed Event doc — the day
	 *  chips in guest-issue-add.view.ts resolve every type's own entry window
	 *  and day membership off these two fields, the same TicketType.* functions
	 *  the storefront's own programme-day selector reads. */
	readonly schedule: Event.Event["schedule"];
	readonly locationId: Event.Event["locationId"];
};

/** The catalog with the organization it was read under, in one value, so a
 *  reader keyed on the organization never takes another organization's
 *  events for its own while a switch is landing. */
export const saleEvents = state<{
	readonly orgId: string | null;
	readonly events: readonly SaleEventOption[];
}>("admin/sale/events").memory({ orgId: null, events: [] });

export type SaleCatalogStatus = "loading" | "ready" | "error";
export const saleCatalogStatus = state<SaleCatalogStatus>(
	"admin/sale/catalog-status",
).memory("loading");

/** The event both sale sub-features currently render — shared so switching
 *  it on one route is remembered on the other (recognition over recall).
 *  null until saleEvents resolves and sale.sync.ts auto-selects one. */
export const selectedSaleEventId = state<string | null>(
	"admin/sale/selected-event",
).memory(null);

export type SaleTicketType = TicketType.TicketType & { readonly id: string };

/** The selected event's own ticket types — re-hydrated by
 *  sale.sync.ts on every selectedSaleEventId change. */
export const saleTicketTypes = state<readonly SaleTicketType[]>(
	"admin/sale/ticket-types",
).memory([]);

export const saleEventOptions = computed(() =>
	[...saleEvents.value.events].sort((a, b) => a.startMs - b.startMs),
);

/** Whether the signed-in actor may read a ticket type's quota doc — mirrors
 *  quota.entity.ts's own member-read gate (tickets.reports.view). No longer
 *  a route or nav-rail gate of its own: events/stats.sync.ts
 *  (web-side) reads this before fetching the events-list footer's own
 *  sold-vs-capacity figures, so an actor without it simply never fetches
 *  (and never sees) any event's footer. */
export const canViewSaleReports = computed(() =>
	[
		...(activeActor.get()?.permissions ?? []),
		...(activeActor.get()?.eventId
			? (activeActor.get()?.eventPermissions ?? [])
			: []),
	].some((permission) =>
		expandPermission("tickets.reports.view").includes(permission),
	),
);

/** Whether the signed-in actor may issue complimentary guest tickets —
 *  mirrors root package.json's ican.extraMatches guest-create write rule
 *  (tickets.guests.issue). Gates both the Guest tickets nav-rail item and
 *  the guest-issue-view route landing. */
export const canIssueGuestTickets = computed(() =>
	[
		...(activeActor.get()?.permissions ?? []),
		...(activeActor.get()?.eventId
			? (activeActor.get()?.eventPermissions ?? [])
			: []),
	].some((permission) =>
		expandPermission("tickets.guests.issue").includes(permission),
	),
);
